// F12.2 — paste-detect heuristic.
//
// Tiny, no-deps language sniffer that the composer modal calls on paste.
// First-match-wins, biased to NULL — we'd rather not auto-switch than
// surprise the user when the heuristic is unsure. Strong signals only.
//
// Rule order matters; TS-strong signals run before JS keywords so that a
// `const x: number = 1` doesn't classify as `js`.

const TS_STRONG = /(?::\s*(?:string|number|boolean|void|never|any|unknown)\b)|(?:^|\n)\s*(?:interface|type|enum)\s+\w/;
// JS keywords — anchor to line start so a stray "const" inside prose doesn't
// trip the rule. `var` is deliberately omitted (too ambiguous — `var x = 1`
// without surrounding code shouldn't auto-switch the picker; spec calls this
// out explicitly). `=>` is a strong-enough fat-arrow signal on its own.
const JS_KW = /(?:^|\n)\s*(?:import\s|export\s|function\s|const\s|let\s)|=>/;
const PY_KW = /(?:^|\n)\s*def\s|\bif\s+__name__\b|(?:^|\n)\s*from\s+\S+\s+import\s/;
const SHELL_SHEBANG = /^(?:#!\/(?:bin|usr\/bin\/env)\b|\$\s)/;
const MD_HEADING = /\n#{1,2}\s|\n```/;
// Very loose CSS detector — selector { prop: val; }
const CSS_RULE = /[.#\w-]+\s*\{[^{}]*:[^{};]+;[^{}]*\}/;
const HTML_DOCTYPE = /^\s*<(?:!doctype\b|html\b|head\b|body\b)/i;

/**
 * Returns the most likely language id, or null if unknown.
 *
 * Bias to NULL when unsure. Empty / very short input always returns null —
 * the composer should not switch the picker for a 2-char paste.
 */
export function detectLanguage(text) {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;

  // HTML first — distinctive prefix, low false-positive rate.
  if (HTML_DOCTYPE.test(trimmed)) return 'html';

  // Shell shebang / prompt prefix.
  if (SHELL_SHEBANG.test(trimmed)) return 'bash';

  // TypeScript strong signals before JS keywords (TS is a JS superset).
  if (TS_STRONG.test(text)) return 'ts';

  // JS keywords.
  if (JS_KW.test(text)) return 'js';

  // Python.
  if (PY_KW.test(text)) return 'py';

  // JSON — strict: trimmed starts with { or [, AND JSON.parse succeeds.
  if (trimmed[0] === '{' || trimmed[0] === '[') {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      /* not valid JSON — fall through */
    }
  }

  // Markdown — look for line-leading `#` heading or fenced block.
  if (MD_HEADING.test(`\n${text}`)) return 'md';

  // CSS — last because the rule is loose enough to false-positive on JS objects.
  if (CSS_RULE.test(text)) return 'css';

  // Unsure — let the user keep their picker choice.
  return null;
}

export default detectLanguage;
